import type { VoiceProductCreateCapabilities } from "./VoiceProductCreateObject";
/**
 * Voice capability — client-side contract.
 *
 * Backend: product/docs/voice-api-contract.md (NineBackend repo) (contract v1)
 * Errors:  nine-generics/docs/voice-error-handling.md (NineBackend repo)
 * UI plan: docs/plans/voice-edit-ui-plan.md
 *
 * Endpoints (all under /api/v1/voice, bearer-authenticated, real HTTP statuses):
 *   GET  /capabilities                      -> contents[0]: VoiceCapabilities
 *   POST /skills/{skillId}/analyze          -> contents[0]: VoiceResult (multipart: audio + context JSON part)
 *   POST /requests/{requestId}/outcome      -> contents: []
 * Confirm / Undo send `interpretation.result` / `.undo` (partial UpdateProductRq
 * for product.edit) to the EXISTING PUT /api/v1/products/update-product/{id}.
 */
import type { ProductDetailsModal } from "./ProductObject";

// ─── Enums (string unions — unknown values must be tolerated) ──────────────

export type VoiceSkillId = "product.edit";
export type VoiceCapabilitySkillId = VoiceSkillId | "product.create";
export type VoiceStatus = "UNDERSTOOD" | "NOT_UNDERSTOOD";
export type VoicePresentation = "CONFIDENT" | "CONFIRM_ONE" | "CHOOSE" | "NOT_UNDERSTOOD";
export type VoiceChangeKind = "PRICE" | "BOOLEAN" | "TEXT" | "STATUS" | "NEW_ITEM";
export type VoiceOutcome = "CONFIRMED" | "APPLY_FAILED" | "UNDONE" | "DISCARDED" | "DRAFT_ACCEPTED" | "PUBLISHED" | "PUBLISH_FAILED";

// ─── Backend DTOs ──────────────────────────────────────────────────────────

export interface VoiceSkillInfo {
  skillId: string;
  version: string;
  enabled: boolean;
}

export interface VoiceCapabilities {
  enabled: boolean;
  skills: VoiceSkillInfo[];
  minDurationMs: number;
  maxDurationMs: number;
  maxAudioBytes: number;
  /** Base MIME types: audio/webm, audio/mp4, audio/ogg, audio/wav, audio/mpeg. */
  acceptedMimeTypes: string[];
  dailyLimit: number;
  usedToday: number;
  productCreate?: VoiceProductCreateCapabilities;
}

/** The `context` part of the analyze request (VoiceAnalyzeRq). */
export interface VoiceAnalyzeContext {
  /** New per recording; reused only for "Send again" of the same clip. */
  clientRequestId: string;
  /** product.edit: the productId. */
  subjectId?: string;
  durationMs: number;
  /** As recorded, e.g. "audio/webm;codecs=opus". */
  mimeType: string;
  locale?: string;
  /** Turbo: deeper, business-aware intelligence. Sent only when on. */
  turbo?: boolean;
}

export interface VoiceChange {
  /** "sellingPrice", "variants[<id>].price", "details.BRAND", "info[<blockId>].body", ... */
  field: string;
  label: string;
  /** Unknown kinds are rendered as TEXT. */
  kind: VoiceChangeKind | string;
  /** null for NEW_ITEM. Numbers as plain decimals ("218", "218.5"). */
  from?: string | null;
  to: string;
  /** Optional working, e.g. "450 + 10% = 495". */
  note?: string | null;
}

export interface VoiceNote {
  /** VoiceError code (16160-16189) or "BIG_CHANGE". */
  code: string;
  field?: string | null;
  label?: string | null;
  detail?: string | null;
}

export interface VoiceInterpretation {
  interpretationId: string;
  /** First person, in the speaker's own language mix. */
  intent: string;
  confidence: number;
  changes: VoiceChange[];
  notApplied?: VoiceNote[] | null;
  warnings?: VoiceNote[] | null;
  /** Skill payload for Confirm (product.edit: partial UpdateProductRq). */
  result: unknown;
  /** Skill payload for Undo. */
  undo?: unknown;
}

export interface VoiceResult {
  requestId: string;
  skillId: string;
  skillVersion: string;
  status: VoiceStatus | string;
  /** Unknown values are treated as CHOOSE. */
  presentation: VoicePresentation | string;
  /** VoiceError code 16150-16155 when NOT_UNDERSTOOD. */
  reasonCode?: string | null;
  reasonDetail?: string | null;
  transcript?: string | null;
  language?: string | null;
  interpretations?: VoiceInterpretation[] | null;
  /** Set only for presentation CONFIDENT. */
  preselectedId?: string | null;
  createdAt?: string | null;
  /** Never confirm after this. */
  expiresAt?: string | null;
}

export interface VoiceOutcomeBody {
  outcome: VoiceOutcome;
  interpretationId?: string;
  errorCode?: string;
  productId?: string;
}

/** What opens the edit popup in voice mode (CreateProduct's `voice` prop). */
export interface VoiceLaunch {
  skillId: VoiceSkillId;
  product: ProductDetailsModal;
}

// ─── Client-side types ─────────────────────────────────────────────────────

/** A finished recording. */
export interface VoiceClip {
  blob: Blob;
  /** As recorded (may include codecs). */
  mimeType: string;
  durationMs: number;
}

/** What the primary button on an error screen does. */
export type VoiceErrorAction = "sendAgain" | "retry" | "close";

/** An error as the Voice step shows it. */
export interface VoiceErrorInfo {
  /** Backend VoiceError code, a VOICE_LOCAL_CODES value, or "HTTP_<status>". */
  code: string;
  message: string;
  retryable: boolean;
  action: VoiceErrorAction;
  /** Shown as "Ref: <first 8 chars>" when present. */
  requestId?: string;
}

// ─── Codes ─────────────────────────────────────────────────────────────────

/** Backend VoiceError codes (nine-generics VoiceError, range 16101-16199). */
export const VOICE_ERROR_CODES = {
  FEATURE_DISABLED: "16101",
  UNAUTHENTICATED: "16102",
  SKILL_NOT_FOUND: "16103",
  SKILL_DISABLED: "16104",
  VALIDATION_ERROR: "16105",
  AUDIO_MISSING: "16106",
  AUDIO_TOO_LARGE: "16107",
  AUDIO_TOO_SHORT: "16108",
  AUDIO_TOO_LONG: "16109",
  AUDIO_UNSUPPORTED_FORMAT: "16110",
  SUBJECT_NOT_FOUND: "16111",
  NOT_ALLOWED: "16112",
  PERMISSION_CHECK_FAILED: "16113",
  SUBJECT_NOT_EDITABLE: "16114",
  DAILY_LIMIT_EXCEEDED: "16115",
  REQUEST_IN_PROGRESS: "16116",
  AT_CAPACITY: "16117",
  TRANSCRIPTION_FAILED: "16118",
  INTERPRETATION_FAILED: "16119",
  PROVIDER_TIMEOUT: "16120",
  PROVIDER_RATE_LIMITED: "16121",
  IDEMPOTENCY_CONFLICT: "16122",
  REQUEST_NOT_FOUND: "16123",
  INVALID_OUTCOME: "16124",
  // "not understood" reasons (inside a 200 result)
  NO_SPEECH: "16150",
  NO_ACTIONABLE_INTENT: "16151",
  ALL_INTERPRETATIONS_INVALID: "16152",
  MODEL_REFUSED: "16153",
  NOTHING_TO_CHANGE: "16154",
  ONLY_UNSUPPORTED: "16155",
  INTERNAL_ERROR: "16199",
} as const;

/** Codes for problems detected in the browser (never sent by the server). */
export const VOICE_LOCAL_CODES = {
  NO_SPEECH: "LOCAL_NO_SPEECH",
  TOO_SHORT: "LOCAL_TOO_SHORT",
  TOO_LARGE: "LOCAL_TOO_LARGE",
  INTERRUPTED: "LOCAL_INTERRUPTED",
  NETWORK: "LOCAL_NETWORK",
  TIMEOUT: "LOCAL_TIMEOUT",
  BAD_RESPONSE: "LOCAL_BAD_RESPONSE",
} as const;

/** Fallbacks used until /capabilities answers (or if it can't be read). */
export const VOICE_DEFAULT_LIMITS = {
  minDurationMs: 600,
  maxDurationMs: 30000,
  maxAudioBytes: 1048576,
  acceptedMimeTypes: ["audio/webm", "audio/mp4", "audio/ogg", "audio/wav", "audio/mpeg"],
};

export type VoiceLimits = typeof VOICE_DEFAULT_LIMITS;

// ─── Copy ──────────────────────────────────────────────────────────────────

export const VOICE_GENERIC_ERROR = "Something went wrong. Please try again.";
export const VOICE_NOT_UNDERSTOOD = "Chintu didn't understand. Please try again.";
export const VOICE_NO_SPEECH = "Chintu couldn't hear anything. Please try again.";
export const VOICE_TOO_SHORT = "That was too short. Hold on a little longer and try again.";
export const VOICE_INTERRUPTED = "Recording stopped. Please try again.";
export const VOICE_MIC_BLOCKED =
  "Microphone is blocked. Allow it in your browser or app settings, then try again.";
export const VOICE_MIC_UNAVAILABLE = "No microphone was found. Connect one and try again.";
export const VOICE_UNSUPPORTED = "Voice isn't supported on this device.";
export const VOICE_EXPIRED = "This result is out of date. Please record again.";
export const VOICE_SELECT_HINT = "Pick what you meant, then tap Confirm.";

const C = VOICE_ERROR_CODES;
const L = VOICE_LOCAL_CODES;

/** Copy + primary action for a request-level error. Section 9 of the UI plan. */
export function voiceErrorCopy(
  code: string | null | undefined,
  errorDetail?: string | null,
  retryable?: boolean
): { message: string; action: VoiceErrorAction; retryable: boolean } {
  switch (code) {
    case L.TOO_SHORT:
    case C.AUDIO_TOO_SHORT:
      return { message: VOICE_TOO_SHORT, action: "retry", retryable: false };
    case C.AUDIO_TOO_LONG:
      return { message: "Please keep it under 30 seconds.", action: "retry", retryable: false };
    case L.TOO_LARGE:
    case C.AUDIO_TOO_LARGE:
    case C.AUDIO_UNSUPPORTED_FORMAT:
    case C.AUDIO_MISSING:
      return { message: "Chintu couldn't use that recording. Please try again.", action: "retry", retryable: false };
    case L.INTERRUPTED:
      return { message: VOICE_INTERRUPTED, action: "retry", retryable: false };
    case C.SUBJECT_NOT_FOUND:
      return { message: "This listing no longer exists.", action: "close", retryable: false };
    case C.NOT_ALLOWED:
      return { message: "You can only edit listings you own or manage.", action: "close", retryable: false };
    case C.SUBJECT_NOT_EDITABLE:
      return { message: "This listing can't be edited right now.", action: "close", retryable: false };
    case C.DAILY_LIMIT_EXCEEDED:
      return {
        message: "You've used today's voice edits. Try again tomorrow, or use Edit.",
        action: "close",
        retryable: false,
      };
    case C.FEATURE_DISABLED:
    case C.SKILL_DISABLED:
    case C.SKILL_NOT_FOUND:
      return { message: "Voice editing isn't available right now.", action: "close", retryable: false };
    case C.UNAUTHENTICATED:
      return { message: "Please sign in again.", action: "close", retryable: false };
    case C.REQUEST_IN_PROGRESS:
      return { message: "Chintu is still working on your last request…", action: "sendAgain", retryable: true };
    case C.VALIDATION_ERROR:
    case C.IDEMPOTENCY_CONFLICT:
      return { message: VOICE_GENERIC_ERROR, action: "retry", retryable: false };
    case L.TIMEOUT:
      return { message: "That took too long. Please try again.", action: "sendAgain", retryable: true };
    case L.NETWORK:
      return {
        message: "Couldn't reach NineFinds. Check your connection and try again.",
        action: "sendAgain",
        retryable: true,
      };
    case C.PERMISSION_CHECK_FAILED:
    case C.AT_CAPACITY:
    case C.TRANSCRIPTION_FAILED:
    case C.INTERPRETATION_FAILED:
    case C.PROVIDER_TIMEOUT:
    case C.PROVIDER_RATE_LIMITED:
    case C.INTERNAL_ERROR:
    case L.BAD_RESPONSE:
      return { message: errorDetail || VOICE_GENERIC_ERROR, action: "sendAgain", retryable: true };
    default: {
      const canResend = retryable !== false;
      return {
        message: errorDetail || VOICE_GENERIC_ERROR,
        action: canResend ? "sendAgain" : "retry",
        retryable: canResend,
      };
    }
  }
}

/** Copy for a NOT_UNDERSTOOD result (reasonCode 16150-16155) or a local no-speech. */
export function voiceReasonCopy(
  reasonCode: string | null | undefined,
  reasonDetail?: string | null
): string {
  switch (reasonCode) {
    case C.NO_SPEECH:
    case L.NO_SPEECH:
      return VOICE_NO_SPEECH;
    case L.TOO_SHORT:
      return VOICE_TOO_SHORT;
    case C.NOTHING_TO_CHANGE:
      return "Already done! Your listing already says that.";
    case C.ONLY_UNSUPPORTED:
      return reasonDetail && reasonDetail.trim()
        ? `Chintu can't change ${reasonDetail.trim()} by voice yet. Use Edit for that.`
        : "Chintu can't change that by voice yet. Use Edit for that.";
    default:
      return VOICE_NOT_UNDERSTOOD;
  }
}

/** Unknown presentation values are treated as CHOOSE (contract §8). */
export function normalizePresentation(value: string | null | undefined): VoicePresentation {
  switch (value) {
    case "CONFIDENT":
    case "CONFIRM_ONE":
    case "CHOOSE":
    case "NOT_UNDERSTOOD":
      return value;
    default:
      return "CHOOSE";
  }
}

/** First 8 characters of a request id, shown as "Ref: …" on failure screens. */
export function voiceRef(requestId: string | null | undefined): string | null {
  return requestId ? requestId.slice(0, 8) : null;
}
